module Client.Page.User.TipBurnData

open System
open Client.Components.Graph.TipBurn
open Client.Infrastructure.Api
open Client.Components
open Client.Domain
open Client.InfrastructureTypes
open Client.DomainTypes.Msg
open Elmish
open Fable.React
open Feliz
open Fulma
open Shared.Dto.Page
open Shared.Dto.Dto
open Shared.Infrastructure
open Client.Components.GraphCommon

type DataModel = {
    RequestRunning: bool
    DateRangePicker: DateRangePicker.Data
    Session: UserSession
    SensorId: int
    Station: TipBurnData.StationDto
    GraphData: Loadable<TipBurnGraph, unit>
}

type LoadingModel = {
    Session: UserSession
    SensorId: int
}

type Model = Loadable<DataModel, LoadingModel>

let init id (session: UserSession) : Model * Cmd<Msg> =
    let requestData = {
        SessionKey = session.SessionKey
        Data = id
    }

    let cmd =
        Cmd.OfAsync.either
            api.getTipBurnStation
            requestData
            (TipBurnDataMsg.StationReceived >> Msg.TipBurnData)
            (TipBurnDataMsg.LoadingStationFailed >> Msg.TipBurnData)

    Loadable.Loading(
        {
            Session = session
            SensorId = id
        }
    ),
    cmd

let private createRequestDataCommand (request: AuthenticatedRequest<TipBurnData.DataRequest>) : Cmd<Msg> =
    Cmd.OfAsync.either
        api.getTipBurnData
        request
        (TipBurnDataMsg.GraphDataReceived >> Msg.TipBurnData)
        (TipBurnDataMsg.LoadingDataFailed >> Msg.TipBurnData)

let private stationResponseToDataModel
    (loadingModel: LoadingModel)
    (response: AuthenticatedResponse<TipBurnData.StationResponse>)
    : Model * Cmd<Msg> =
    match response with
    | Ok station ->
        match station with
        | TipBurnData.Data data ->
            let startTimestamp = DateTime.Now |> DateTime.addDays -2 |> DateTime.startOfDay
            let endTimestamp = DateTime.Now |> DateTime.addDays 1 |> DateTime.endOfDay

            let requestData: AuthenticatedRequest<TipBurnData.DataRequest> = {
                SessionKey = loadingModel.Session.SessionKey
                Data = {
                    StationId = loadingModel.SensorId
                    StartTimestamp = startTimestamp
                    EndTimestamp = endTimestamp |> DateTime.endOfDay
                }
            }

            Loadable.Data {
                RequestRunning = true
                DateRangePicker = DateRangePicker.initWithRange startTimestamp endTimestamp (TimeSpan.FromDays 31) true
                Session = loadingModel.Session
                SensorId = loadingModel.SensorId
                Station = data
                GraphData = Loadable.Loading()
            },
            createRequestDataCommand requestData
        | TipBurnData.NotFound -> Loadable.Error "Tipburn-Station nicht gefunden", Cmd.none
    | AuthenticatedResponse.Error authError ->
        match authError with
        | AuthErr Unauthenticated -> Loadable.Error "Sie sind nicht eingeloggt, bitte die Seite neu laden", Cmd.none
        | AuthErr Unauthorized -> Loadable.Error "Tipburn-Station nicht gefunden", Cmd.none
        | CustomErr err -> Loadable.Error "Beim Laden der Tipburn-Station ist ein Fehler aufgetreten", Cmd.none


let update (msg: TipBurnDataMsg) (model: Model) : Model * Cmd<Msg> =
    match msg, model with
    | TipBurnDataMsg.StationReceived stationResponse, Loadable.Loading loadingModel ->
        stationResponseToDataModel loadingModel stationResponse
    | TipBurnDataMsg.GraphDataReceived dataResponse, Loadable.Data dataModel ->
        match dataResponse with
        | Ok(TipBurnData.DataResponse.Data data) ->
            let graphData =
                if List.isEmpty data then
                    TipBurnGraph.NoData
                else
                    TipBurnGraph.Data(createGraphData data)

            Loadable.Data {
                dataModel with
                    RequestRunning = false
                    GraphData = Loadable.Data graphData
            },
            Cmd.none
        | Ok(TipBurnData.DataResponse.NotFound) ->
            let cmd =
                ErrorConfirmation.responseError ErrorConfirmation.defaultConfig (CustomErr "")

            Loadable.Data { dataModel with RequestRunning = false }, cmd
        | AuthenticatedResponse.Error err ->
            let cmd = ErrorConfirmation.responseError ErrorConfirmation.defaultConfig err

            Loadable.Data { dataModel with RequestRunning = false }, cmd
    | TipBurnDataMsg.GraphDateRangeChanged dates, Loadable.Data dataModel ->
        let newPickerModel = DateRangePicker.updateData dates dataModel.DateRangePicker

        Loadable.Data { dataModel with DateRangePicker = newPickerModel }, Cmd.none
    | TipBurnDataMsg.RequestData request, Loadable.Data dataModel ->
        let authenticatedRequest = {
            SessionKey = dataModel.Session.SessionKey
            Data = request
        }

        Loadable.Data { dataModel with RequestRunning = true }, createRequestDataCommand authenticatedRequest
    | TipBurnDataMsg.LoadingStationFailed _, _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | TipBurnDataMsg.LoadingDataFailed ex, Loadable.Data dataModel ->
        let cmd =
            ErrorConfirmation.dataTransmissionErrorConfirmation ErrorConfirmation.defaultConfig ex

        Loadable.Data dataModel, cmd
    | _, _ -> model, Cmd.none

let private graphWrappingView (graphs: ReactElement list) = div [] [ yield! graphs ]

let private boxHeader: ReactElement =
    Heading.h3 [
        Heading.Modifiers [
            Modifier.TextAlignment(Screen.All, TextAlignment.Option.Centered)
        ]
    ] [ str "Visualisierung der Daten" ]

let private createRequestData (stationId: int) (startDate: DateTime) (endDate: DateTime) : TipBurnData.DataRequest = {
    StationId = stationId
    StartTimestamp = startDate
    EndTimestamp = endDate
}

let createEnabledButtonOptions dispatch (stationId: int) (startDate: DateTime) (endDate: DateTime) =
    let requestData =
        createRequestData stationId (DateTime.startOfDay startDate) (DateTime.endOfDay endDate)

    [
        Button.Disabled false
        Button.OnClick(fun _ -> dispatch (TipBurnDataMsg.RequestData requestData |> TipBurnData))
    ]

let private dateRangePicker dispatch (model: DataModel) =
    let buttonOptions =
        Option.map2
            (createEnabledButtonOptions dispatch model.SensorId)
            model.DateRangePicker.Start
            model.DateRangePicker.End
        |> Option.defaultValue [ Button.Disabled true ]

    let buttonOptions =
        List.append buttonOptions [
            Button.IsLink
            Button.IsLoading model.RequestRunning
        ]

    Column.column [ Column.Width(Screen.All, Column.IsFull) ] [
        Columns.columns [ Columns.IsVCentered ] [
            Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ p [] [ str "Datumsbereich:" ] ]
            Column.column [] [
                DateRangePicker.view
                    "Von/Bis Datumsbereich auswählen"
                    ((TipBurnDataMsg.GraphDateRangeChanged >> TipBurnData) >> dispatch)
                    model.DateRangePicker
            ]
            Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ Button.button buttonOptions [ str "Laden" ] ]
        ]
    ]

let private pageHeader (stationName: string) : ReactElement =
    Heading.h1 [
        Heading.Modifiers [
            Modifier.TextAlignment(Screen.All, TextAlignment.Centered)
        ]
    ] [ str stationName ]

let private pageLayout stationName content =
    Container.container [ Container.isFullWidth ] [
        pageHeader stationName
        Columns.columns [ Columns.IsMultiline ] [
            Column.column [ Column.Width(Screen.All, Column.IsFull) ] [ Box.box' [] content ]
        ]
    ]

let private graphsView (graphData: TipBurnGraph) =
    match graphData with
    | NoData -> [
        createFullColumn (boxStringSubtitle "Es wurden keine Daten im ausgewählten Zeitraum gefunden")
      ]
    | TipBurnGraph.Data data -> [
        riskGraph data
        temperatureGraph None data
        humidityGraph None data
        wetnessGraph None data
      ]


let pageView dispatch (model: DataModel) =
    let boxContent = [
        boxHeader
        dateRangePicker dispatch model
        yield! (Loadable.listView graphsView model.GraphData)
    ]

    pageLayout model.Station.Name boxContent


let view dispatch (model: Model) = Loadable.view (pageView dispatch) model